import React, { useState, useEffect } from 'react'
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Box,
  Typography,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  IconButton
} from '@mui/material'
import {
  Group as GroupIcon,
  Search as SearchIcon,
  Clear as ClearIcon
} from '@mui/icons-material'
import { SelectChangeEvent } from '@mui/material/Select'
import { useTeamsWithCounts } from '../hooks/useTeamEnrichedData'

interface TeamFilterProps {
  selectedTeams: string[]
  onTeamsChange: (teams: string[]) => void
  disabled?: boolean
}

interface Team {
  name: string
  count?: number
}

const TeamFilter: React.FC<TeamFilterProps> = ({
  selectedTeams,
  onTeamsChange,
  disabled = false
}) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')
  const [isOpen, setIsOpen] = useState(false)

  // Debounce search term to avoid immediate filtering
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm)
    }, 300) // 300ms delay

    return () => clearTimeout(timer)
  }, [searchTerm])

  // Fetch teams with counts in a single optimized call
  const {
    data: teamsWithCountsData,
    isLoading: teamsLoading,
    error: teamsError
  } = useTeamsWithCounts()

  const teams = React.useMemo(() => {
    if (!teamsWithCountsData) return []

    // Data already comes with counts from the optimized endpoint
    let enrichedTeams = teamsWithCountsData.map((team: { name: string; count: number }) => ({
      name: team.name,
      count: team.count
    }))

    // Apply search filter using debounced search term
    if (debouncedSearchTerm.trim()) {
      const term = debouncedSearchTerm.toLowerCase()
      enrichedTeams = enrichedTeams.filter((team: Team) =>
        team.name.toLowerCase().includes(term)
      )
    }

    // Sort alphabetically by team name
    enrichedTeams.sort((a: Team, b: Team) => {
      return a.name.localeCompare(b.name)
    })

    return enrichedTeams
  }, [teamsWithCountsData, debouncedSearchTerm])

  const loading = teamsLoading
  const error = teamsError



  const handleChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value
    const newTeams = typeof value === 'string' ? value.split(',') : value
    onTeamsChange(newTeams)
  }

  const handleChipDelete = (teamToDelete: string) => {
    const newTeams = selectedTeams.filter(team => team !== teamToDelete)
    onTeamsChange(newTeams)
  }

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value)
  }

  const handleClearSearch = () => {
    setSearchTerm('')
  }

  const handleOpen = () => {
    setIsOpen(true)
    // Auto-focus on search input when dropdown opens
    setTimeout(() => {
      const searchInput = document.querySelector('#team-search-input') as HTMLInputElement
      if (searchInput) {
        searchInput.focus()
      }
    }, 100)
  }

  const handleClose = () => {
    setIsOpen(false)
    setSearchTerm('')
  }

  const clearAll = () => {
    console.log('Team Filter Clear All clicked')
    onTeamsChange([])
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 200 }}>
        <CircularProgress size={20} />
        <Typography variant="body2" color="text.secondary">
          Loading teams...
        </Typography>
      </Box>
    )
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ minWidth: 200 }}>
        {error instanceof Error ? error.message : 'Failed to load teams'}
      </Alert>
    )
  }

  return (
    <Box sx={{
      minWidth: 180, // Reduced from 200 to 180
      height: '40px',
      display: 'flex',
      alignItems: 'center',
      position: 'relative'
    }}> {/* Dynamic width based on content */}
      <FormControl
        fullWidth
        size="small"
        sx={{
          '& .MuiInputBase-root': {
            alignItems: 'baseline'
          }
        }}
      >
        <InputLabel id="team-filter-label" sx={{ fontSize: '0.8rem' }}>Team</InputLabel>
        <Select
          labelId="team-filter-label"
          id="team-filter"
          multiple
          value={selectedTeams}
          onChange={handleChange}
          open={isOpen}
          onClose={handleClose}
          onOpen={handleOpen}
          label="Team"
          disabled={disabled}
          renderValue={(selected) => (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.25 }}> {/* Reduced gap from 0.5 to 0.25 */}
              {selected.map((value) => (
                <Chip
                  key={value}
                  label={value}
                  size="small"
                  onDelete={() => handleChipDelete(value)}
                  onMouseDown={(event) => {
                    event.stopPropagation()
                  }}
                  sx={{
                    height: 20, // Slightly smaller chip height
                    fontSize: '0.75rem' // Smaller font size
                  }}
                />
              ))}
            </Box>
          )}
          MenuProps={{
            PaperProps: {
              style: {
                maxHeight: 350,
                minWidth: 'auto', // Dynamic width based on content
              },
            },
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'left',
            },
            transformOrigin: {
              vertical: 'top',
              horizontal: 'left',
            },
          }}
        >
          {/* Search Input */}
          <Box sx={{ p: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
            <TextField
              id="team-search-input"
              size="small"
              placeholder="Search teams..."
              value={searchTerm}
              onChange={handleSearchChange}
              fullWidth
              autoFocus
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={handleClearSearch}
                      aria-label="clear search"
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              onMouseDown={(e) => e.stopPropagation()}
              onKeyDown={(e) => {
                // Allow arrow keys to navigate through menu items
                if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                  return // Don't stop propagation for arrow keys
                }
                e.stopPropagation()
              }}
            />
          </Box>

          {selectedTeams.length > 0 && (
            <MenuItem
              onMouseDown={(e) => {
                e.preventDefault()
                e.stopPropagation()
                clearAll()
              }}
              sx={{
                fontStyle: 'italic',
                color: 'primary.main',
                borderBottom: '1px solid',
                borderColor: 'divider',
                mb: 1
              }}
            >
              Clear all selections
            </MenuItem>
          )}
          
          {teams.map((team) => (
            <MenuItem key={team.name} value={team.name} sx={{ py: 0.75 }}> {/* Reduced padding from 1 to 0.75 */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, width: '100%' }}> {/* Reduced gap from 1 to 0.75 */}
                <GroupIcon fontSize="small" color="primary" />
                <Typography variant="body2" sx={{ flexGrow: 1, fontSize: '0.875rem' }}> {/* Slightly smaller font */}
                  {team.name}
                </Typography>
                {team.count !== undefined && (
                  <Chip
                    label={team.count}
                    size="small"
                    variant="outlined"
                    color="primary"
                    sx={{
                      ml: 0.75, // Reduced from 1 to 0.75
                      fontSize: '0.7rem', // Reduced from 0.75rem to 0.7rem
                      height: '18px', // Reduced from 20px to 18px
                      '& .MuiChip-label': {
                        px: 0.75 // Reduced from 1 to 0.75
                      }
                    }}
                  />
                )}
              </Box>
            </MenuItem>
          ))}
          
          {teams.length === 0 && (
            <MenuItem disabled>
              <Typography variant="body2" color="text.secondary">
                {searchTerm ? `No teams found for "${searchTerm}"` : 'No teams available'}
              </Typography>
            </MenuItem>
          )}
        </Select>
      </FormControl>

      {/* Remove the "X teams selected" text that was causing GUI issues */}
    </Box>
  )
}

export default TeamFilter
